import { useEffect, useMemo, useState } from "react";
import {
    MapPointProps,
    MapRouteProps,
} from "../../components/framework/map/map";
import { WorkShiftTimeSlotItem } from "../../models/workShitTimeSlot/workShiftTimeSlotItem";
import { getVehiclePositionsBatch } from "../../services/vehicleService";
import { Base } from "../../framework/base";
import { useReload } from "../useReload";
import { getWorkTimeTypeColorCode } from "../../models/workShitTimeSlot/workTimeType";
import { VehiclePositionBatchQueryParams } from "../../models/vehicle/vehiclePosition";

interface WorkShiftTimeSlotRoute {
    [id: string]: MapRouteProps;
}

interface WorkShiftTimeSlotPoint {
    [id: string]: MapPointProps;
}

export interface WorkShiftTimeSlotMapData {
    routes: WorkShiftTimeSlotRoute;
    points: WorkShiftTimeSlotPoint;
}

export const useWorkShiftTimeSlotMapData = (
    workShiftTimeSlots: WorkShiftTimeSlotItem[],
    selectedId?: string,
    hoveredId?: string
) => {
    const [mapData, setMapData] = useState<WorkShiftTimeSlotMapData>();
    const [loading, setLoading] = useState(false);
    const [queryParams, setQueryParams] = useState<VehiclePositionBatchQueryParams[]>();
    const [key, reload] = useReload();

    useEffect(() => {
        const params = workShiftTimeSlots
            .map(
                (w, i) => ({
                    ...w,
                    vehicleId:
                        !w.vehicleId &&
                            new WorkShiftTimeSlotItem(w).isBreak()
                            ? workShiftTimeSlots[i - 1]?.vehicleId
                            : w.vehicleId,
                } as WorkShiftTimeSlotItem)
            )
            .filter((w) => w.vehicleId)
            .map(
                (w) => ({
                    id: w.id,
                    vehicleId: w.vehicleId,
                    windowStartTime: w.startDate,
                    windowEndTime:
                        w.endDate || Base.dayjsToJsonDateTimeOffset(),
                } as VehiclePositionBatchQueryParams)
            );

        // Only update if query params have been changed.
        if (!params || JSON.stringify(params) !== JSON.stringify(queryParams)) {
            setQueryParams(params);
        }
    }, [
        workShiftTimeSlots
            .map((s) => s.rowId)
            .sort()
            .join(","),
    ]);

    useEffect(() => {
        const abortController = new AbortController();

        if (queryParams?.length > 0) {
            setLoading(true);

            getVehiclePositionsBatch(
                workShiftTimeSlots
                    .map(
                        (w, i) => ({
                            ...w,
                            vehicleId:
                                !w.vehicleId &&
                                    new WorkShiftTimeSlotItem(w).isBreak()
                                    ? workShiftTimeSlots[i - 1]?.vehicleId
                                    : w.vehicleId,
                        } as WorkShiftTimeSlotItem)
                    )
                    .filter((w) => w.vehicleId)
                    .map((w) => ({
                        id: w.id,
                        vehicleId: w.vehicleId,
                        windowStartTime: w.startDate,
                        windowEndTime:
                            w.endDate || Base.dayjsToJsonDateTimeOffset(),
                    }))
            )
                .then((res) => {
                    const routeBySlot: WorkShiftTimeSlotRoute = {};
                    const pointBySlot: WorkShiftTimeSlotPoint = {};
                    const data = res?.data ?? {};

                    Object.keys(data).forEach((id) => {
                        if (data[id]?.length > 0) {
                            routeBySlot[id] = {
                                coords: data[id]
                                    .sort((a, b) =>
                                        Base.dayjsCompare(
                                            a.timestamp,
                                            b.timestamp
                                        )
                                    )
                                    .map((d) => ({
                                        lat: d.latitude,
                                        lng: d.longitude,
                                    })),
                            };

                            pointBySlot[id] = {
                                id: id,
                                coords: routeBySlot[id].coords[0],
                            };
                        }
                    });

                    setMapData({ routes: routeBySlot, points: pointBySlot });
                })
                .catch(console.error)
                .finally(() => setLoading(false));
        }

        return () => {
            abortController.abort();
        };
    }, [key, queryParams]);

    const routes = useMemo(
        () =>
            mapData
                ? workShiftTimeSlots
                    .map((s) => {
                        const isHighlighted = selectedId === s.id || hoveredId === s.id;
                        const isHovered = hoveredId === s.id;
                        const isSelected = selectedId === s.id;
                        const r = mapData.routes[s.id];
                        return r
                            ? {
                                ...r,
                                strokeColor: getWorkTimeTypeColorCode(s.workTimeTypeType),
                                dataId: s.id,
                                highlight: isHighlighted,
                                // Also override highlight colors
                                ...(isHighlighted && {

                                    strokeWeight: isSelected ? 4 : isHovered ? 7 : 3,
                                    zIndex: isSelected ? 2 : isHovered ? 3 : 1,
                                    strokeOpacity: 1,
                                })
                            }
                            : null;
                    })
                    .filter(Boolean)
                : null,
        [mapData, workShiftTimeSlots, selectedId, hoveredId]
    );

    const points = useMemo(
        () =>
            mapData
                ? workShiftTimeSlots
                    .filter((s) => !selectedId || selectedId === s.id)
                    .map((s) => {
                        const p = mapData.points[s.id];
                        const slot = new WorkShiftTimeSlotItem(s);
                        return p
                            ? {
                                ...p,
                                text: slot.getMapToolTipText(),
                                textColorClass:
                                    slot.getContrastColorClass(),
                                textBgColorClass: slot.getColorClass(),
                                groupName: slot.workTimeTypeName,
                                groupColor: getWorkTimeTypeColorCode(
                                    slot.workTimeTypeType
                                ),
                            }
                            : null;
                    })
                    .filter(Boolean)
                : null,
        [mapData, workShiftTimeSlots, selectedId]
    );

    return { routes, points, reload, loading };
};